import { DatePicker, Select, Spin } from 'antd';
import moment, { Moment } from 'moment';
import { RangeValue } from 'rc-picker/lib/interface';
import React, { useCallback, useEffect, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import '../styles.css';
import { formatHour, generateDataBarChart, shifts } from '../helpers';
import { chartsService } from 'services/modulos/chartsService';
import { toast } from 'react-toastify';

const { Option } = Select;

const ShiftBarChart = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedShift, setSelectedShift] = useState<keyof typeof shifts>(
    'madrugada'
  );
  const [selectedMonth, setSelectedMonth] = useState<Moment | null>(
    moment().add(-1, 'month')
  );

  const [selectedGroupType, setSelectedGroupType] = useState<'AIS' | 'REGION'>(
    'AIS'
  );

  const [dataAPI, setDataApi] = useState<ReturnVtrEnpenhadas[]>([]);
  const [data, setData] = useState<
    {
      ais: string;
      purple: number;
      orange: number;
    }[]
  >([]);

  const { start, end } = shifts[selectedShift];

  const handleChangeFilters = useCallback(
    (field: string, value: string) => {
      let params: { [key: string]: string } = {};

      if (field === 'shift') {
        setSelectedShift(value as keyof typeof shifts);
        params.shift = value;
        params.groupType = selectedGroupType;
      } else if (field === 'groupType') {
        setSelectedGroupType(value as 'AIS' | 'REGION');
        params.shift = selectedShift;
        params.groupType = value;
      }

      const newData = generateDataBarChart(
        dataAPI,
        params.shift,
        params.groupType as 'AIS' | 'REGION'
      );

      setData(newData || []);
    },
    [selectedGroupType, selectedShift, dataAPI]
  );

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      if (!selectedMonth) {
        toast.info('Informe o mês da pesquisa');
        return;
      }

      const body = {
        dataInicio: selectedMonth
          .startOf('month')
          .format('YYYY-MM-DDTHH:mm:ss')
          .substring(0, 19),
        dataFim: selectedMonth
          .endOf('month')
          .format('YYYY-MM-DDTHH:mm:ss')
          .substring(0, 19),
      };

      const response = await chartsService.getVtrEmpenhadas(body);

      const newData = generateDataBarChart(
        response,
        selectedShift,
        selectedGroupType
      );

      setDataApi(response);
      setData(newData || []);
    } catch (e) {
    } finally {
      setIsLoading(false);
    }
  }, [selectedMonth, selectedShift, selectedGroupType]);

  useEffect(() => {
    fetchData();
  }, [selectedMonth]);

  return (
    <div
      style={{
        width: '100%',
        height: 500,
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '20px',
        position: 'relative',
      }}
    >
      <h1 style={{ font: 'bold' }}>Média de ocupação por turno</h1>

      <div className="bar-chart-filter">
        <div className="default-flex-div">
          <label
            style={{
              fontWeight: '600',
              color: 'grey',
            }}
          >
            Mês:
          </label>
          <DatePicker
            value={selectedMonth}
            onChange={e => setSelectedMonth(e)}
            picker="month"
          />
        </div>

        <div className="default-flex-div">
          <label
            style={{
              fontWeight: '600',
              color: 'grey',
            }}
          >
            Turno:
          </label>

          <Select
            value={selectedShift}
            onChange={e => handleChangeFilters('shift', e)}
            showSearch
            style={{ width: 200 }}
            virtual={false}
            // mode="tags"
            notFoundContent={null}
            getPopupContainer={trigger => trigger.parentElement}
            placeholder="turno"
            filterOption={(input, option) => {
              return Boolean(
                option?.children &&
                  String(option.children || '')
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
              );
            }}
          >
            {Object.keys(shifts).map(shift => (
              <Option key={shift} value={shift}>
                {shift}
              </Option>
            ))}
          </Select>
        </div>

        <div className="default-flex-div">
          <label
            style={{
              fontWeight: '600',
              color: 'grey',
            }}
          >
            Mostrar por:
          </label>

          <Select
            value={selectedGroupType}
            onChange={e => handleChangeFilters('groupType', e)}
            showSearch
            style={{ width: 200 }}
            virtual={false}
            // mode="tags"
            notFoundContent={null}
            getPopupContainer={trigger => trigger.parentElement}
            placeholder="agrupar por"
            filterOption={(input, option) => {
              return Boolean(
                option?.children &&
                  String(option.children || '')
                    .toLowerCase()
                    .indexOf(input.toLowerCase()) >= 0
              );
            }}
          >
            <Option key={1} value={'AIS'}>
              AIS
            </Option>
            <Option key={2} value={'REGION'}>
              REGIÃO
            </Option>
          </Select>
        </div>
      </div>

      {isLoading && (
        <div
          style={{
            position: 'absolute',
            opacity: '50%',
            backgroundColor: '#d3d3d3',
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Spin />
        </div>
      )}

      <ResponsiveContainer>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="ais" />
          <YAxis
            domain={[start, end]}
            tickFormatter={value => formatHour(value, 'madrugada')}
            label={{ value: 'Turno', angle: -90, position: 'insideLeft' }}
          />
          <Tooltip
            formatter={value => formatHour(Number(value), selectedShift)}
          />
          <Legend />
          <Bar dataKey="purple" fill="purple" name="Tempo médio de ocupação" />
          <Bar
            dataKey="orange"
            fill="orange"
            name="Tempo médio de desocupação"
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ShiftBarChart;
